import React, { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { Url } from '@wix/da-url';
import { PapiUser } from '@wix/da-types-papi';
import UserLink from '@wix/da-shared-react/pkg/User/Link/UserLink';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import ToggleButtonGroup from '@wix/da-shared-react/pkg/ToggleButtonGroup';
import GalleryTorpedoIcon from '@wix/da-shared-react/pkg/Icons/24x24/GalleryTorpedo';
import StackedIcon from '@wix/da-shared-react/pkg/Icons/24x24/Stacked';
import GhostButton from '@wix/da-shared-react/pkg/Buttons/GhostButton';
import { SessionContext } from '@wix/da-react-context/pkg/SessionContext';
import { BiEventType } from '@wix/da-bi/pkg/constants';
import type { BrowseItem } from '../../../../../types/api';
import DeviantsBar from '../../../DeviantsBar';
import { isDeviation } from '../../../../helpers/streams';
import BasePageLayout from '../../_partials/BasePageLayout';
import DefaultPageContent from '../../_partials/DefaultPageContent';
import WatchButton from '../../../WatchButton';
import WatchedUserMenu from '../../../WatchedUserMenu';
import s from './WatchPage.scss';

export interface Props {
  feedType?: string;
  thisUser?: PapiUser;
  isStacked?: boolean;
  isEmpty?: boolean;
  watchCount: number;
}

const WatchPage: React.FC<Props> = ({
  feedType,
  thisUser,
  isStacked,
  watchCount,
  isEmpty,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const { user } = useContext(SessionContext);
  const isOwnPage =
    !!user &&
    !!thisUser &&
    (user === thisUser.userId ||
      (user as PapiUser).userId === thisUser ?.userId);

  const getItemUrl = useCallback(
    (item: BrowseItem) => {
      if (!isDeviation(item) || !item.author) {
        return;
      }

      return Url.browseDeviantsYouWatchLink(
        feedType as any,
        item.author.username
      );
    },
    [feedType]
  );

  if (thisUser) {
    return (
      <BasePageLayout
        title={<UserLink user={thisUser} className={s['username']} />}
        customHeader={<DeviantsBar />}
        headerIsStickyOnScrollUp
        tagLine={
          isMobile ? (
            <div className={s['extras']}>
              <div className={s['watch-button-container']}>
                <div />
                <WatchButton
                  user={thisUser}
                  className={(s['watch-button'])}
                />
                <div>
                  <WatchedUserMenu user={thisUser} />
                </div>
              </div>
            </div>
          ) : undefined
        }
        headerRightSideContent={
          isMobile ? undefined : (
            <>
              <UserLink
                user={thisUser}
                className={s['rightside-button']}
                biData={{ sectionname: 'button' }}
              >
                <GhostButton
                  size="small"
                  variant="unbranded"
                  isFakeButton
                  className={s['visit-button']}
                >
                  {t('subnavigation.button.visitProfile')}
                </GhostButton>
              </UserLink>
              {!isOwnPage && (
                <div className={s['rightside-button']}>
                  <WatchButton className={s['watch-button']} user={thisUser} />
                </div>
              )}
            </>
          )
        }
      >
        <DefaultPageContent />
      </BasePageLayout>
    );
  }

  const showViewSwitcher = feedType === 'deviations' && !isEmpty;

  return (
    <BasePageLayout
      title={t('subnavigation.title.watch')}
      tagLine={
        isMobile || isEmpty
          ? undefined
          : t('subnavigation.subtitle.watchingDeviants', {
            count: watchCount,
          })
      }
      customHeader={!isEmpty && <DeviantsBar />}
      withNavigation={!isEmpty}
      headerIsStickyOnScrollUp={!isEmpty}
      contentClassName={classnames(
        s['content'],
        showViewSwitcher && s['with-view-switcher']
      )}
    >
      {showViewSwitcher && (
        <div className={s['view-switcher']}>
          <ToggleButtonGroup
            value={isStacked ? 'stack' : 'grid'}
            onChange={(value) => {
              if (value === 'stack') {
                window.location.assign('?stacked=1')
              }
              if (value === 'grid') {
                window.location.assign('?stacked=0')
              }
            }}
            buttons={[
              {
                tooltipText: t('view.grid.button.tooltip'),
                ariaLabel: t('view.grid.button.a11y.label'),
                icon: GalleryTorpedoIcon,
                value: 'grid',
                biData: {
                  evid: BiEventType.WATCH_GROUP_BY_ARTIST_CLICK,
                  is_grouped: isStacked ? 1 : 0,
                }
              },
              {
                tooltipText: t('view.stack.button.tooltip'),
                ariaLabel: t('view.stack.button.a11y.label'),
                icon: StackedIcon,
                value: 'stack',
                biData: {
                  evid: BiEventType.WATCH_GROUP_BY_ARTIST_CLICK,
                  is_grouped: isStacked ? 1 : 0,
                }
              }
            ]}
          />
        </div>
      )}
      <DefaultPageContent
        getItemUrl={isStacked ? getItemUrl : undefined}
        contentStripIndex={3}
      />
    </BasePageLayout>
  );
};
WatchPage.displayName = 'WatchPage';

export default WatchPage;
